/**
 * 触摸优化常量定义
 * Feature: mobile-responsive-framework, Task 4: 实现触摸优化系统
 * 需求: 3.1, 3.2, 3.3, 3.4
 */

// 触摸优化配置接口
export interface TouchOptimizationConfig {
  minTouchTarget: number;     // 最小触摸目标尺寸 (44px - WCAG标准)
  minButtonHeight: number;    // 最小按钮高度
  minInputHeight: number;     // 最小输入框高度
  minListItemHeight: number;  // 最小列表项高度
  buttonSpacing: number;      // 按钮间距
  listItemSpacing: number;    // 列表项间距
  touchPadding: number;       // 触摸区域内边距
}

// 默认触摸优化配置
export const TOUCH_CONFIG: TouchOptimizationConfig = {
  minTouchTarget: 44,       // 44px是WCAG推荐的最小触摸目标 (需求3.1)
  minButtonHeight: 44,      // 按钮最小高度44px
  minInputHeight: 44,       // 输入框最小高度44px (需求3.2)
  minListItemHeight: 48,    // 列表项最小高度48px
  buttonSpacing: 8,         // 按钮间距最小8px (需求3.3)
  listItemSpacing: 4,       // 列表项间距最小4px (需求3.4)
  touchPadding: 8           // 触摸区域内边距
} as const;

// 触摸目标尺寸等级
export type TouchTargetSize = 'small' | 'medium' | 'large';

export const TOUCH_TARGET_SIZES: Record<TouchTargetSize, number> = {
  small: 32,    // 小型触摸目标 (仅桌面端)
  medium: 44,   // 中型触摸目标 (推荐)
  large: 56     // 大型触摸目标 (重要操作)
} as const;

// 间距等级
export type SpacingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const TOUCH_SPACING: Record<SpacingSize, number> = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24
} as const;

// 组件触摸配置
export interface ComponentTouchConfig {
  minHeight: number;
  minWidth: number;
  padding: string;
  margin: string;
}

// 各组件的触摸优化配置
export const COMPONENT_TOUCH_CONFIGS: Record<string, ComponentTouchConfig> = {
  button: {
    minHeight: 44,
    minWidth: 44,
    padding: '8px 16px',
    margin: '4px'
  },
  input: {
    minHeight: 44,
    minWidth: 0,
    padding: '8px 12px',
    margin: '0'
  },
  select: {
    minHeight: 44,
    minWidth: 0,
    padding: '8px 12px',
    margin: '0'
  },
  checkbox: {
    minHeight: 44,
    minWidth: 44,
    padding: '10px',
    margin: '4px'
  },
  radio: {
    minHeight: 44,
    minWidth: 44,
    padding: '10px',
    margin: '4px'
  },
  listItem: {
    minHeight: 48,
    minWidth: 0,
    padding: '12px 16px',
    margin: '2px 0'
  },
  menuItem: {
    minHeight: 48,
    minWidth: 0,
    padding: '12px 16px',
    margin: '0'
  },
  tab: {
    minHeight: 44,
    minWidth: 64,
    padding: '8px 16px',
    margin: '0 4px'
  },
  iconButton: {
    minHeight: 44,
    minWidth: 44,
    padding: '10px',
    margin: '4px'
  }
} as const;

/**
 * 检测是否为触摸设备
 * @returns 是否为触摸设备
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - msMaxTouchPoints是IE/Edge特有属性
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * 检测是否为粗指针设备（触摸屏）
 * @returns 是否为粗指针设备
 */
export function isCoarsePointer(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/**
 * 检测是否支持悬停
 * @returns 是否支持悬停
 */
export function supportsHover(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(hover: hover)').matches;
}

/**
 * 获取组件的触摸优化配置
 * @param componentType 组件类型
 * @param isTouchEnabled 是否启用触摸优化
 * @returns 触摸配置
 */
export function getComponentTouchConfig(
  componentType: keyof typeof COMPONENT_TOUCH_CONFIGS,
  isTouchEnabled: boolean
): ComponentTouchConfig {
  const config = COMPONENT_TOUCH_CONFIGS[componentType];
  
  if (!isTouchEnabled) {
    // 非触摸设备使用较小的尺寸
    return {
      ...config,
      minHeight: Math.max(config.minHeight - 12, 32),
      minWidth: config.minWidth > 0 ? Math.max(config.minWidth - 12, 32) : 0
    };
  }
  
  return config;
}

/**
 * 计算安全的触摸目标尺寸
 * @param desiredSize 期望尺寸
 * @param isTouchDevice 是否为触摸设备
 * @returns 安全的触摸目标尺寸
 */
export function getSafeTouchTargetSize(desiredSize: number, isTouchDevice: boolean): number {
  if (!isTouchDevice) return desiredSize;
  return Math.max(desiredSize, TOUCH_CONFIG.minTouchTarget);
}

/**
 * 计算安全的按钮间距
 * @param desiredSpacing 期望间距
 * @param isTouchDevice 是否为触摸设备
 * @returns 安全的按钮间距
 */
export function getSafeButtonSpacing(desiredSpacing: number, isTouchDevice: boolean): number {
  if (!isTouchDevice) return desiredSpacing;
  return Math.max(desiredSpacing, TOUCH_CONFIG.buttonSpacing);
}

/**
 * 计算安全的列表项间距
 * @param desiredSpacing 期望间距
 * @param isTouchDevice 是否为触摸设备
 * @returns 安全的列表项间距
 */
export function getSafeListItemSpacing(desiredSpacing: number, isTouchDevice: boolean): number {
  if (!isTouchDevice) return desiredSpacing;
  return Math.max(desiredSpacing, TOUCH_CONFIG.listItemSpacing);
}
